/* eslint no-unused-vars: ["warn", { "varsIgnorePattern": "[iI]gnored" }] */

import { restHelper } from '../helpers';
import constants from '../constants';
import { getReverseAddresses, setReverseAddresses } from "../helpers/storageHelper";
const { HERE_API_KEY } = constants;
// INFO we currently use freemium tier APP_ID / APP_CODE to keep support on long term restricted to some features functionalities only
// https://my-air.atlassian.net/browse/MYP-2573
const { HERE_APP_ID, HERE_APP_CODE } = constants;

// https://developer.here.com/documentation/places

// https://developer.here.com/documentation/geocoder-autocomplete/topics/resource-suggest.html
const AUTOCOMPLETE_URL = 'https://autocomplete.search.hereapi.com/v1/autocomplete';
const PLACES_AUTOSUGGEST_URL = 'https://autosuggest.search.hereapi.com/v1/autosuggest';
const GEOCODE_URL = 'https://geocode.search.hereapi.com/v1/geocode'; // INFO check if won't be used
const ROUTEMATCHING_URL = 'https://routematching.hereapi.com/v8/match/routelinks';
const REVERSE_GEOCODE_URL = 'https://revgeocode.search.hereapi.com/v1/revgeocode';
const ROUTING_URL = 'https://router.hereapi.com/v8/routes';
const LOOKUPBYID_URL = 'https://lookup.search.hereapi.com/v1/lookup';

const LTS__AUTOCOMPLETE_URL = 'https://autocomplete.geocoder.api.here.com/6.2/suggest.json';
const LTS__GEOCODE_URL = 'https://geocoder.api.here.com/6.2/geocode.json';
const LTS__REVERSE_GEOCODE_URL = 'https://reverse.geocoder.api.here.com/6.2/reversegeocode.json';
// 
const SMAP_API_URL = "https://smap.hereapi.com/v8/maps";

// offsetKm: distance of each satellite call from center (km)
// offsetDirs: 4 = N/S/E/W only; 8 = N/NE/E/SE/S/SW/W/NW (9 total calls incl. center)
// 8/12-direction pattern is needed when the admin area is larger diagonally than along cardinals.
const radiusLayerByShapeLevelMap = new Map([
  ['default',    { layer: 'ADMIN_POLY_8', radius: 40000,  offsetKm: null, offsetDirs: 4 }],
  ['country',    { layer: 'ADMIN_POLY_0', radius: 250000, offsetKm: 470,  offsetDirs: 12 }], // 470+250=720 coverage, 12 directions fills SSE/SSW gaps
  ['state',      { layer: 'ADMIN_POLY_1', radius: 250000, offsetKm: 150,  offsetDirs: 8 }], // 150+250=400km coverage
  ['county',     { layer: 'ADMIN_POLY_2', radius: 150000, offsetKm: null, offsetDirs: 4 }],
  ['city',       { layer: 'ADMIN_POLY_8', radius: 40000,  offsetKm: null, offsetDirs: 4 }],
  ['district',   { layer: 'ADMIN_POLY_8', radius: 40000,  offsetKm: null, offsetDirs: 4 }],
  ['postalCode', { layer: 'ADMIN_POLY_8', radius: 40000,  offsetKm: null, offsetDirs: 4 }],
])

// Split a MULTIPOLYGON content string (everything inside MULTIPOLYGON(...))
// into individual polygon entries. Each entry is bounded by (( and )).
// The separator between adjacent polygons is always ")),((" so we split on that.
const splitPolygonEntries = (content) => {
  const parts = content.split(')),((');
  if (parts.length === 1) {
    return [content];
  }
  return parts.map((p, i) => {
    if (i > 0) {
      p = '((' + p;
    }                   
    if (i < parts.length - 1) {
      p = p + '))';
    }
    return p;
  });
};

// Merge multiple MULTIPOLYGON WKT strings into one, deduplicating at
// the individual sub-polygon level so overlapping tile results don't
// add redundant polygons to the renderer.
const mergeMultipolygonWKT = (wktArray) => {
  if (wktArray.length === 1) {
    return wktArray[0];
  }

  const seen = new Set();
  const uniquePolys = [];

  for (const wkt of wktArray) {
    const m = wkt.trim().match(/^MULTIPOLYGON\((.+)\)$/);
    const content = m ? m[1] : wkt;
    for (const poly of splitPolygonEntries(content)) {
      if (!seen.has(poly)) {
        seen.add(poly);
        uniquePolys.push(poly);
      }
    }
  }

  return 'MULTIPOLYGON(' + uniquePolys.join(',') + ')';
}

//

class HereService {
  constructor() {
    this.apiKey = HERE_API_KEY;
    // INFO we currently use freemium tier APP_ID / APP_CODE to keep support on long term restricted to some features functionalities only
    // https://my-air.atlassian.net/browse/MYP-2573
    this.appId = HERE_APP_ID;
    this.appCode = HERE_APP_CODE;
    //
  }

  // usage: setVehicleType({ engineType: 'diesel', fuelConsumption: '5.0' });
  // https://developer.here.com/documentation/routing/dev_guide/topics/resource-param-type-vehicle-type.html
  setVehicleType = ({ engineType, fuelConsumption }) => {
    this.engineType = engineType;
    this.fuelConsumption = fuelConsumption;
  };

  clearVehicleType = () => {
    this.engineType = undefined;
    this.fuelConsumption = undefined;
  };

  /**
   *
   * @param {*} searchText
   * @param {*} language IT, EN
   * @param {*} resultType houseNumber, intersection, street, postalCode, district, city, county, state, country
   */
  autocomplete(query, language = 'IT', resultType = 'street') {
    return restHelper.get(
        `${AUTOCOMPLETE_URL}?apiKey=${this.apiKey}&q=${query}&beginHighlight=<b>&endHighlight=</b>&lang=${language}`
    ).then((response) => {
      try {
        let { items } = response;
        items = items.map(item => {
          return {
            label: item.title,
            locationId: item.id
          }
        });
        return { suggestions: items };
      } catch (error) {
        return Promise.reject({ error, message: 'No data available' });
      }
    });
  }

  autocompleteLTS(query, language = 'IT', resultType = 'street') {
    return restHelper.get(
      `${LTS__AUTOCOMPLETE_URL}?app_id=${this.appId}&app_code=${this.appCode}&query=${query}&beginHighlight=<b>&endHighlight=</b>&language=${language}&resultType=${resultType}`
    );
  }

  geocode(q, language = 'IT', shapeLevel = 'default') {
    return restHelper.get(`${GEOCODE_URL}?apiKey=${this.apiKey}&q=${q}&language=${language}&additionaldata=IncludeShapeLevel,${shapeLevel}`).then((response) => {
      try {
        return response;
      } catch (error) {
        return Promise.reject({ error, message: 'No data available' });
      }
    });
  }

  geocodeLTS(locationid, language = 'IT', shapeLevel = 'default') {
    return restHelper.get(`${LTS__GEOCODE_URL}?app_id=${this.appId}&app_code=${this.appCode}&locationid=${locationid}&language=${language}&additionaldata=IncludeShapeLevel,${shapeLevel}`);
  }

  lookupbyid(locationid, language = 'IT', shapeLevel = 'default') {
    return restHelper.get(`${LOOKUPBYID_URL}?apiKey=${this.apiKey}&id=${locationid}&language=${language}`).then((response) => {
      try {
        return response;
      } catch (error) {
        return Promise.reject({ error, message: 'No data available' });
      }
    });
  }

  reverseGeocodeUncached(lat, lng, radius, mode = 'retrieveAddresses', shapeLevel = 'default') {
    const at = `${lat}%2C${lng}`;
    return restHelper.get(`${REVERSE_GEOCODE_URL}?apiKey=${this.apiKey}&at=${at}&lang=it-IT`)
  }

  reverseGeocodeUncachedLTS(lat, lng, radius, mode = 'retrieveAddresses', shapeLevel = 'default') {
    let prox = `${lat},${lng},${radius}`;
    return restHelper.get(`${LTS__REVERSE_GEOCODE_URL}?prox=${prox}&mode=${mode}&app_id=${this.appId}&app_code=${this.appCode}&additionaldata=IncludeShapeLevel,${shapeLevel}`);
  }

  reverseGeocode = async ( lat, lng, radius, mode = 'retrieveAddresses') => {
    const object = await getReverseAddresses({ latitude: lat, longitude: lng });
    if (object !== undefined && object.hasOwnProperty('data')) {
      return object.data;
    }
    return await this.reverseGeocodeUncached( lat, lng, radius, mode)
      .then( async ({ items }) => {
        try {
          const address = items[0].address;
          await setReverseAddresses({ latitude: lat, longitude: lng, data: address })
          return address;
        } catch (error) {
          return Promise.reject({ error, msg: 'Address is not available' });
        }
      });
    // return await this.reverseGeocodeUncached( lat, lng, radius, mode)
    // .then( async (response) => {
    //   try {
    //     const data = this.transformHERErevgeocode(response);
    //     await setReverseAddresses({ latitude: lat, longitude: lng, data })
    //     return data;
    //   } catch (error) {
    //     return Promise.reject({ error, msg: 'Address is not available' });
    //   }
    // });
  }

  matchroute(gpx) {
    return restHelper.postXml(`${ROUTEMATCHING_URL}?apiKey=${this.apiKey}&mode=fastest;car;traffic:disabled&routeMatch=1`, { xml: gpx });
  }

  places(q, at, resultType = 'place', size = 10) {
    return restHelper.get(`${PLACES_AUTOSUGGEST_URL}?apiKey=${this.apiKey}&q=${q}&at=${at}&result_types=${resultType}&limit=${size}`).then((response) => {
      try {
        // let { results } = response;
        // return results;
        let { items } = response;
        items = items.filter(item => item.hasOwnProperty('position') && item.position !== undefined).map(item => {
          let category = "generic";
          if (item.administrativeAreaType) {
            category = item.administrativeAreaType;
          } else if (item.localityType) {
            category = item.localityType;
          }
          return {
            id: item.id,
            highlightedTitle: item.title,
            position: [item.position.lat, item.position.lng],
            category: category,
            vicinity: item.address.label
          }
        });
        return items;
      } catch (error) {
        return Promise.reject({ error, message: 'No data available' });
      }
    })
  }

  retrieveAddress = async (lat, lon, radius = '250') => {
    let address = await this.reverseGeocode(lat, lon, radius);
    return address.label;
  }

  getAdminBoundaryPolygon = async (lat, lng, shapeLevel, name) => {
    const radiusLayer = radiusLayerByShapeLevelMap.get(shapeLevel);
    if (!radiusLayer) {
      return Promise.reject({ error: 'Invalid shape level' });
    }

    const { layer, radius, offsetKm, offsetDirs } = radiusLayer;

    // Build query points: center + satellite points to cover areas beyond the API's 250km limit.
    // Each satellite is offsetKm away from center; with a 250km query radius the total reach
    // from center is offsetKm + 250km.
    // Center call is always first (queryPoints[0]) — it anchors the ADMIN_PLACE_ID.
    // Satellite calls are placed at offsetKm in offsetDirs evenly-spaced directions
    // (regular polygon around the center). Any number of directions is supported.
    const queryPoints = [{ lat, lng }];
    if (offsetKm) {
      const lngKm = 111 * Math.cos(lat * Math.PI / 180);
      for (let i = 0; i < offsetDirs; i++) {
        const angle = (2 * Math.PI * i) / offsetDirs; // evenly spaced, starting North
        queryPoints.push({
          lat: lat + (offsetKm / 111)   * Math.cos(angle),
          lng: lng + (offsetKm / lngKm) * Math.sin(angle),
        });
      }
    }

    // queryPoints[0] is always the original geocoded point (center).
    // It determines WHICH admin area we want — the one that contains this point.
    // The offset calls (queryPoints[1..4]) only expand tile coverage for that same area.

    // Fire all calls in parallel
    const responses = await Promise.allSettled(
      queryPoints.map(p =>
        restHelper.get(`${SMAP_API_URL}/attributes?apiKey=${this.apiKey}&layers=${layer}&in=proximity:${p.lat},${p.lng};r=${radius}`)
      )
    );

    // Step 1: identify the target ADMIN_PLACE_ID from the center call.
    // Prefer a name match (handles enclaves like San Marino inside Italy),
    // fall back to geometries[0] which is the API's top relevance result.
    const centerGeoms = responses[0].status === 'fulfilled'
      ? (responses[0].value.geometries || [])
      : [];

    if (centerGeoms.length === 0) {
      return Promise.reject({ error: 'No administrative boundary available' });
    }

    let targetGeom = null;
    if (name) {
      const nameLower = name.toLowerCase();
      targetGeom = centerGeoms.find(g => {
        const gName = (g.attributes && (g.attributes.NAME || '')).toLowerCase();
        return gName && (gName.includes(nameLower) || nameLower.includes(gName));
      });
    }
    if (!targetGeom) {
      targetGeom = centerGeoms[0];
    }

    const targetPlaceId = targetGeom.attributes.ADMIN_PLACE_ID;

    // Step 2: collect tile pieces for the target ADMIN_PLACE_ID from ALL calls,
    const allPieces = [];

    for (const r of responses) {
      if (r.status === "fulfilled") {
        for (const g of r.value.geometries || []) {
          if ((g.attributes && g.attributes.ADMIN_PLACE_ID) !== targetPlaceId) {
            // not the same admin area as center point, skip
            continue;
          }
          allPieces.push(g.geometry);
        }
      }
    }

    if (allPieces.length === 0) {
      return Promise.reject({ error: 'No administrative boundary available' });
    }

    // Step 3: merge all tile pieces into one MULTIPOLYGON
    return mergeMultipolygonWKT(allPieces);
  }

  route = async ({ fromLatLng, toLatLng }) => {
    const origin = `${fromLatLng.lat},${fromLatLng.lng}`;
    const destination = `${toLatLng.lat},${toLatLng.lng}`;
    return restHelper.get(
      `${ROUTING_URL}?apiKey=${this.apiKey}&origin=${origin}&destination=${destination}&routingMode=fast&transportMode=car&return=summary&units=metric&lang=it-IT`
    );
  };

  routeSummary = ({ fromLatLng, toLatLng }) => {
    return this.route({ fromLatLng, toLatLng }).then((response) => {
      try {
        const data = this.transformHERErouting(response);
        return data;
      } catch (error) {
        return Promise.reject({ error, message: 'No route available' });
      }
    });
  };

  transformHERErevgeocode = (input) => {
    // const { Response } = input;
    // const address = Response.View[0].Result[0].Location.Address;
    // delete address.AdditionalData;
    // const output = address;
    let { items: [ { address } ] } = input;
    let output = {};
    for (const key in address) {
      const k = key[0].toUpperCase() + key.slice(1);
      output[k] = address[key];
    }
    return output;
  };

  transformHERErouting = (input) => {
    // const { response: { route: [ { summary } ] } } = input;
    // return summary;
    const { routes: [ { sections: [ { summary } ] } ] } = input;
    const { duration: trafficTime, length: distance } = summary;
    const output = { trafficTime, distance };
    return output;
  };

}

export default HereService;



// WEBPACK FOOTER //
// ./src/services/Here.js